import {
  Box,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import PageTitle from '../../components/page-title/PageTitle';
import { Container, LinkImageDetailing } from './FollowupList.styled';
import * as yup from 'yup';
import IFormError from '../../interfaces/IFormError';
import { useValidateSchema } from '../../hooks/providers/useValidateSchema';

import { AiFillCloseCircle } from 'react-icons/ai';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import {
  CameraIcon,
  IconContainer,
  InputAreaDetailing,
  InputFile,
} from '../wizard/steps/medicalCertificateImage/MedicalCertificateImage.styles';
import imageCompression from 'browser-image-compression';
import { Delete as DeleteIcon } from '@mui/icons-material';
import toastr from 'toastr';
import IMedicalCertificate from '../../interfaces/IMedicalCertificate';
import { useMedicalCertificateService } from '../../hooks/services/useMedicalCertificateService';

interface IProps {
  closeDialog: () => void;
  isOpen: boolean;
  medicalCertificate: IMedicalCertificate | null;
  handleRequest: (message: string) => Promise<void>;
}

const FollowupEditDeadline = (props: IProps) => {
  const handleClose = () => {
    setErrors(null);
    setJustification('');
    setDateDeadline(null);
    setFiles([]);
    setLoading(false);
    props.closeDialog();
  };
  const request = useMedicalCertificateService();

  const [errors, setErrors] = useState<IFormError | null>(null);
  const [justification, setJustification] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [medicalCertificate, setMedicalCertificate] = useState<IMedicalCertificate | null>(props.medicalCertificate);
  const [dateDeadline, setDateDeadline] = useState<Moment | null>(null);
  const inputFile = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const validation = useValidateSchema();

  const schema = yup.object({
    justification: yup
      .string()
      .required('Campo obrigatório')
      .min(1, 'Campo obrigatório'),
    documentDate: yup
      .date()
      .typeError('Formato de data inválido. Use: DD/MM/YYYY')
      .required('Data obrigatória'),
  });

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    const file = e.target.files?.item(0);
    if (file) {
      setFiles(prevFiles => [...prevFiles, file]);
    }
    setLoading(false);
  };

  const sendFile = async (fileParam: File | null) => {
    if (!fileParam) return;

    setLoading(true);
    let file = fileParam;

    if (file.size > 5242880) {
      setLoading(false);
      toastr.error('O tamanho máximo da imagem é 5MB');
      return;
    }

    if (file.size >= 1000000 && file.size <= 5242880 && fileParam.type !== 'application/pdf') {
      const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
      file = await imageCompression(fileParam, options);
    }

    const data = new FormData();
    data.append('attachment', file, file.name);

    await request
      .sendAttachment(`${props.medicalCertificate?.id}`, data)
      .then(() => {
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        toastr.error(err.message);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const updateDeadline = async (
    idMedicalCertificate: string | undefined,
    justification: string,
    dateFormated: string,
  ) => {
    if (!idMedicalCertificate) {
      toastr.error('Selecione uma imagem ao menos');
      setLoading(false);
      return;
    }
    setLoading(true);

    await request
      .updateDeadline(idMedicalCertificate, justification, dateFormated)
      .then(() => {
        setLoading(false);
        setJustification('');
        setDateDeadline(null);
        props.closeDialog();
        toastr.success('Prazo final atualizado com sucesso!');
      })
      .catch((err: { message: any }) => {
        toastr.error(err.message);

        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEndDate = async () => {
    const dateFormated = moment(dateDeadline).toISOString();
    setLoading(true);

    const result = await validation.validate(schema, {
      justification,
      documentDate: dateDeadline?.toDate(),
    });

    setErrors(result.errors);

    if (!result.isValid) {
      setLoading(false);
      return;
    }

    setMedicalCertificate({
      ...medicalCertificate!,
      endTime: moment(dateDeadline).format('YYYY-MM-DD'),
    });

    try {
      await updateDeadline(props.medicalCertificate?.id, justification, dateFormated);

      await Promise.all(files.map(file => sendFile(file)));
      // toastr.success('Prazo atualizado com sucesso!');
      return true;
    } catch (err) {
      toastr.error((err as any)?.message || 'Erro ao atualizar o prazo.');
      return false;
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = async () => {
    if (files.length === 0) {
      toastr.error('Selecione pelo menos uma foto ou documento.');
      return;
    }
    await updateEndDate();
  };

  const removeAttachmentByIndex = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Container>
      <PageTitle title="Alterar Prazo">
        <Tooltip title="Fechar Alteração Prazo" placement="left">
          <IconButton onClick={handleClose}>
            <AiFillCloseCircle />
          </IconButton>
        </Tooltip>
      </PageTitle>

      <Grid container spacing={2} sx={{ padding: '20px 0' }}>
        <Grid item sm={12}>
          <TextField
            label="Justificativa"
            placeholder="Informe porque a data será alterada"
            fullWidth
            value={justification}
            onChange={e => setJustification(e.target.value)}
            error={Boolean(errors?.justification)}
            helperText={errors?.justification}
            autoFocus
          />
        </Grid>

        <Grid item sm={12}>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="pt-br">
            <DatePicker
              label="Nova data final do documento"
              value={dateDeadline}
              onChange={date => setDateDeadline(date)}
              renderInput={(params: TextFieldProps) => (
                <TextField
                  {...params}
                  fullWidth
                  error={Boolean(errors?.documentDate)}
                  helperText={errors?.documentDate}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Grid container sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <h3>Escolher foto</h3>
        <Typography variant="subtitle2">(Carta Médica)</Typography>
        <InputFile type="file" ref={inputFile} onChange={onFileChange} accept="application/pdf,image/*" />

        <InputAreaDetailing>
          <IconContainer>
            <CameraIcon onClick={() => inputFile.current?.click()} />
          </IconContainer>
        </InputAreaDetailing>
      </Grid>

      {files.map((file, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 1,
            px: 1,
            py: 0.5,
          }}
        >
          <LinkImageDetailing target="_blank" href={file.name}>
            {file?.name}
          </LinkImageDetailing>
          <IconButton sx={{ ml: 2 }} onClick={() => removeAttachmentByIndex(index)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      {loading && <LinearProgress />}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
        <Button onClick={handleClose} variant="contained" color="secondary" sx={{ marginRight: 2 }}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Confirmar
        </Button>
      </Box>
    </Container>
  );
};

export default FollowupEditDeadline;
